/**
 * Transcript Module
 * Manages transcript rendering, highlighting, auto-follow scrolling, and user interactions
 */

import appState from '../../core/app-state.js';
import logger from '../../core/logger.js';
import { logEvent } from '../../core/event-logger.js';
import { EVENT_TYPES } from '../../core/event-types.js';
import { 
  TRANSCRIPT_CACHE_LIMIT, 
  SENTENCE_HIGHLIGHT_FPS,
  TRANSCRIPT_CENTER_OFFSET_PX,
  TRANSCRIPT_SCROLL_DURATION_MS
} from '../../config/constants.js';
import { i18nService } from '../../services/i18n-service.js';
import { createSVGIcon } from '../../core/router.js';
import type { Clip } from '../../types/clip.js';
import type { 
  TranscriptSentence, 
  ParsedSentence,
  TranscriptWindow,
  WordInteractionPayload
} from '../../types/transcript.js';

/**
 * Get the replay icon SVG markup
 */
export function getReplayIconSVG(): string {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="pointer-events: none;">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" style="pointer-events: none;"></path>
      <path d="M3 3v5h5" style="pointer-events: none;"></path>
    </svg>
  `;
}

/**
 * Manage transcript cache size to prevent memory bloat
 */
export function manageTranscriptCacheSize(): void {
  if (appState.transcriptCache.size > TRANSCRIPT_CACHE_LIMIT) {
    // Get the first key (oldest entry in insertion order) and remove it
    const firstKey = appState.transcriptCache.keys().next().value;
    if (firstKey) {
      appState.transcriptCache.delete(firstKey);
    }
  }
}

/**
 * Parse plain text transcript into sentences
 */
export function parseTranscriptIntoSentences(text: string, clipId: string): ParsedSentence[] {
  // Check if we already have this transcript parsed in the cache
  // Note: Using transcriptCache for temporary sentence parsing (separate from HTML cache)
  if (appState.transcriptCache.has(clipId)) {
    const cached = appState.transcriptCache.get(clipId);
    // If it's a parsedsentences array (stored as JSON string), parse it
    if (cached) {
      try {
        return JSON.parse(cached) as ParsedSentence[];
      } catch {
        // If not JSON, fall through to re-parse
      }
    }
  }
  
  // Simple sentence splitting (periods followed by space or end)
  // Note: This is a simple implementation and could be improved with NLP techniques
  const sentenceRegex = /([^.!?]+[.!?]+)(?:\s|$)/g;
  const sentences: ParsedSentence[] = [];
  let match: RegExpExecArray | null;
  let index = 0;
  
  while ((match = sentenceRegex.exec(text)) !== null) {
    const matchText = match[1];
    if (matchText) {
      sentences.push({
        text: matchText.trim(),
        index: index++
      });
    }
  }
  
  // Handle remaining text if any
  const lastMatchEnd = sentenceRegex.lastIndex;
  if (lastMatchEnd < text.length) {
    const remainingText = text.substring(lastMatchEnd).trim();
    if (remainingText) {
      sentences.push({
        text: remainingText,
        index: index
      });
    }
  }
  
  // If parsing resulted in no sentences, just use the whole text
  if (sentences.length === 0 && text.trim()) {
    sentences.push({
      text: text.trim(),
      index: 0
    });
  }
  
  // Cache the result as JSON string (shares cache with HTML but different format)
  appState.transcriptCache.set(clipId, JSON.stringify(sentences));
  
  // Manage cache size to prevent memory bloat
  manageTranscriptCacheSize();
  
  return sentences;
}

/**
 * Backfill sentence end timestamps
 */
export function backfillSentenceEndTimestamps(clip: Clip): Clip {
  if (!clip || !clip.sentences || clip.sentences.length === 0) {
    return clip;
  }
  
  // Create a clone to avoid mutating the original directly
  const updatedClip = JSON.parse(JSON.stringify(clip));
  const sentences = updatedClip.sentences;
  
  // Calculate end timestamp for each sentence
  for (let i = 0; i < sentences.length; i++) {
    // If end is already defined, no need to calculate
    if (sentences[i].end !== undefined) continue;
    
    if (i < sentences.length - 1) {
      // For all but last sentence, end time is the start time of the next sentence
      sentences[i].end = sentences[i + 1].start;
    } else {
      // For the last sentence, end time is either:
      // 1. clip duration if available
      // 2. start time + estimated duration based on text length
      if (updatedClip.lengthSec) {
        sentences[i].end = updatedClip.lengthSec;
      } else {
        // Estimate ~200ms per character as a rough fallback
        const estimatedDuration = sentences[i].text.length * 0.2;
        sentences[i].end = sentences[i].start + Math.max(1.5, estimatedDuration);
      }
    }
  }
  
  return updatedClip;
}

/**
 * Handler for word interactions (stub function per requirements)
 */
export function onWordInteract(payload: WordInteractionPayload): void {
  // No visible UI change yet, just log interaction for development
  logger.info('word interaction stub', payload);
}

/**
 * Announce messages to screen readers
 */
export function announceToScreenReader(message: string): void {
  // Create or get SR-only element
  let srAnnouncer = document.getElementById('sr-announcer');
  if (!srAnnouncer) {
    srAnnouncer = document.createElement('div');
    srAnnouncer.id = 'sr-announcer';
    srAnnouncer.setAttribute('aria-live', 'polite');
    srAnnouncer.className = 'sr-only';
    document.body.appendChild(srAnnouncer);
  }
  
  // Set the message
  srAnnouncer.textContent = message;
}

/**
 * Helper function to create a consistent t-sentence with t-word spans
 */
export function createConsistentSentenceElement(sentence: TranscriptSentence, idx: number): HTMLDivElement {
  // Create sentence container as a flex container to allow proper positioning of replay button
  const sentenceEl = document.createElement('div'); // Changed to div for better layout control
  sentenceEl.className = 't-sentence';
  sentenceEl.dataset['sidx'] = String(idx);
  
  // Inner content container for text (prevents layout shift when adding replay button)
  const textContainer = document.createElement('span');
  textContainer.className = 't-sentence-text';
  
  // Make sentence keyboard accessible
  sentenceEl.setAttribute('tabindex', '0');
  sentenceEl.setAttribute('role', 'button');
  sentenceEl.setAttribute('aria-label', `Play from sentence ${idx + 1}`);
  
  // Split text into words
  const words = sentence.text.split(/\s+/);
  words.forEach((word: string, widx: number) => {
    // Create word span with consistent structure
    const wordEl = document.createElement('span');
    wordEl.className = 't-word';
    wordEl.dataset['widx'] = String(widx);
    wordEl.dataset['word'] = word.replace(/[^\w'-]/g, ''); // Clean word without punctuation
    wordEl.textContent = word + (widx < words.length - 1 ? ' ' : '');
    
    // Add to text container (delegation handles clicks)
    textContainer.appendChild(wordEl);
  });
  
  // Add text container to sentence container first (important for layout)
  sentenceEl.appendChild(textContainer);
  
  // Add timestamp if available
  if (sentence.start !== undefined) {
    sentenceEl.dataset['start'] = String(sentence.start);
    
    // Add enhanced keyboard event listeners for accessibility if timestamp is available
    sentenceEl.addEventListener('keydown', function(e: KeyboardEvent) {
      // On Enter or Space, focus the replay button instead of playing directly
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        
        // Find and focus the replay button if it exists
        const replayButton = sentenceEl.querySelector<HTMLButtonElement>('.t-replay-button');
        if (replayButton) {
          // Log keyboard navigation event
          logEvent(EVENT_TYPES['TRANSCRIPT_SENTENCE_KEYBOARD_FOCUS']!, {
            sentenceIndex: idx,
            key: e.key
          });
          
          replayButton.focus();
        }
      }
      
      // Focus replay button on Tab + R (alternative keyboard shortcut) - keeping this unchanged
      else if (e.key === 'r' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        
        // Find and focus the replay button if it exists
        const replayButton = sentenceEl.querySelector<HTMLButtonElement>('.t-replay-button');
        if (replayButton) {
          replayButton.focus();
        }
      }
    });
    
    // Create replay button - only if timestamp is available
    const replayButton = document.createElement('button');
    replayButton.className = 't-replay-button';
    replayButton.setAttribute('aria-label', `Replay sentence ${idx + 1}`);
    replayButton.setAttribute('title', 'Replay this sentence');
    replayButton.setAttribute('type', 'button'); // Ensure it's a proper button
    replayButton.innerHTML = getReplayIconSVG();
    
    logger.debug('Created replay button', { sentenceIndex: idx, timestamp: sentence.start });
    
    // Add click handler to the replay button
    replayButton.addEventListener('click', function(this: HTMLButtonElement, e: MouseEvent) {
      logger.debug('Replay button direct click handler (fallback, should not be called if transcript panel handles it)');
      
      e.preventDefault();
      e.stopPropagation(); // Prevent triggering sentence click handler
      
      // This handler should not be called if the transcript panel is properly handling clicks
      // But keep it as a fallback
      const timestamp = parseFloat(String(sentence.start));
      logger.debug('Fallback replay', { timestamp });
      
      // Add visual feedback
      this.classList.add('clicked');
      setTimeout(() => {
        this.classList.remove('clicked');
        // Force hide the button if sentence is no longer active
        const currentSentenceEl = this.closest('.t-sentence');
        if (currentSentenceEl && !currentSentenceEl.matches(':hover') && 
            !currentSentenceEl.classList.contains('current-sentence') && 
            !currentSentenceEl.classList.contains('active')) {
          this.style.opacity = '0';
          this.style.backgroundColor = 'transparent';
        }
      }, 300);
      
      // Log analytics event
      logEvent(EVENT_TYPES['TRANSCRIPT_SENTENCE_REPLAY_TRIGGER']!, {
        sentenceIndex: idx,
        timestamp: timestamp
      });
      
      // Try to play audio
      const audio = document.querySelector('audio');
      if (audio) {
        try {
          audio.currentTime = Math.max(0, Math.min(timestamp, audio.duration || 9999));
          audio.play().catch(error => {
            logger.error('Fallback play failed', error);
            const playBtn = document.getElementById('playPauseBtn');
            if (playBtn) playBtn.click();
          });
        } catch (error) {
          logger.error('Fallback audio control error', error);
        }
      }
      
      // Import seekToTimestamp here to avoid circular dependency
      // This will be called from main.js context where seekToTimestamp is available
      const win = window as TranscriptWindow;
      if (win.seekToTimestamp) {
        win.seekToTimestamp(timestamp, { source: 'replay', sentenceIndex: idx });
      }
    });
    
    // Append replay button to sentence container (after text container)
    sentenceEl.appendChild(replayButton);
  }
  
  return sentenceEl;
}

/**
 * Clean up transcript event listeners and elements
 */
export function removeTranscriptEventListeners(): void {
  // Clean up transcript panel event listeners
  const transcriptContainer = document.getElementById('transcriptContainer');
  
  // Remove any existing container from the DOM
  if (transcriptContainer) {
    // Remove from DOM to clean up all event listeners
    transcriptContainer.remove();
  }
  
  // Make sure we also remove any other transcript elements that might exist
  const transcriptToggle = document.getElementById('transcriptToggle');
  if (transcriptToggle) {
    transcriptToggle.remove();
  }
  
  const transcriptToggleBtn = document.getElementById('transcriptToggleBtn');
  if (transcriptToggleBtn) {
    transcriptToggleBtn.remove();
  }
  
  // Reset global transcript elements reference
  const win = window as TranscriptWindow;
  win._transcriptElements = undefined;
  
  // Remove keyboard listener if it was previously attached
  if (handleEscapeKey) {
    document.removeEventListener('keydown', handleEscapeKey);
  }
  
  // Reset document body classes
  document.body.classList.remove('transcript-open');
  
  // Reset transcript panel state
  appState.transcriptPanelOpen = false;
  
  logger.info('Transcript elements and event listeners removed');
}

// Note: handleEscapeKey will be defined below after it's declared
let handleEscapeKey: ((event: KeyboardEvent) => void) | null = null;

/**
 * Set the escape key handler (to be called from main initialization)
 */
export function setEscapeKeyHandler(handler: (event: KeyboardEvent) => void): void {
  handleEscapeKey = handler;
}

/**
 * Handle Escape key press to close transcript
 */
export function createEscapeKeyHandler(): (event: KeyboardEvent) => void {
  return function(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      // Only handle if transcript panel is currently open according to appState
      if (appState.transcriptPanelOpen) {
        const transcriptContainer = document.getElementById('transcriptContainer');
        const transcriptToggle = document.getElementById('transcriptToggle');
        const playerTranscriptBtn = document.getElementById('transcriptToggleBtn');
        
        if (transcriptContainer) {
          // Close the panel using the unified function, focus the toggle button
          updateTranscriptPanelState(false, transcriptContainer, transcriptToggle, playerTranscriptBtn);
          event.preventDefault();
        }
      }
    }
  };
}

/**
 * Shared internal function to update transcript panel state
 */
export function updateTranscriptPanelState(
  isOpen: boolean, 
  transcriptContainer: HTMLElement | null, 
  transcriptToggle: HTMLElement | null, 
  focusTarget: HTMLElement | null
): void {
  // Early return if state is already what we're trying to set
  if (isOpen === appState.transcriptPanelOpen) return;
  
  // Update state
  appState.transcriptPanelOpen = isOpen;
  
  if (!transcriptContainer) return;
  
  if (isOpen) {
    // Show container
    transcriptContainer.classList.remove('hidden');
    
    // Add body class for mobile to prevent background scrolling
    document.body.classList.add('transcript-open');
    
    // Update toggle button attributes if it exists
    if (transcriptToggle) {
      transcriptToggle.setAttribute('aria-expanded', 'true');
      transcriptToggle.setAttribute('aria-pressed', 'true');
      transcriptToggle.classList.add('transcript-active');
      transcriptToggle.setAttribute('title', i18nService.getString('hideTranscript') || 'Hide transcript');
      // If using aria-label instead of visible text
      if (transcriptToggle.hasAttribute('aria-label')) {
        transcriptToggle.setAttribute('aria-label', i18nService.getString('hideTranscript') || 'Hide transcript');
      }
    }
    
    // Always update the player toggle button
    const playerToggleBtn = document.getElementById('transcriptToggleBtn');
    if (playerToggleBtn) {
      playerToggleBtn.setAttribute('aria-expanded', 'true');
      playerToggleBtn.setAttribute('aria-pressed', 'true');
      playerToggleBtn.classList.add('transcript-active');
      playerToggleBtn.setAttribute('title', i18nService.getString('hideTranscript') || 'Hide transcript');
      // If using aria-label instead of visible text
      if (playerToggleBtn.hasAttribute('aria-label')) {
        playerToggleBtn.setAttribute('aria-label', i18nService.getString('hideTranscript') || 'Hide transcript');
      }
    }
    
    // Restore scroll position using the Map-based storage
    if (appState.currentClip) {
      const savedScroll = appState.transcriptScroll.get(appState.currentClip.id);
      if (savedScroll && savedScroll > 0) {
        // Use double requestAnimationFrame to ensure DOM is fully updated
        // This prevents race conditions with layout calculations
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const transcriptPanel = transcriptContainer.querySelector('.transcript-panel');
            if (transcriptPanel) {
              transcriptPanel.scrollTop = savedScroll;
            }
          });
        });
      }
    }
    
    // Announce to screen readers - single announcement with distinctive message
    announceToScreenReader('Transcript shown.');
  } else {
    // Store scroll position in the Map before closing (if clip ID is available)
    if (appState.currentClip) {
      const transcriptPanel = transcriptContainer.querySelector('.transcript-panel');
      if (transcriptPanel && transcriptPanel.scrollTop > 0) {
        // Store in the dedicated Map for better lifecycle management
        appState.transcriptScroll.set(appState.currentClip.id, transcriptPanel.scrollTop);
      }
    }
    
    // Hide container
    transcriptContainer.classList.add('hidden');
    
    // Remove body class for mobile
    document.body.classList.remove('transcript-open');
    
    // Update toggle button attributes if it exists
    if (transcriptToggle) {
      transcriptToggle.setAttribute('aria-expanded', 'false');
      transcriptToggle.setAttribute('aria-pressed', 'false');
      transcriptToggle.classList.remove('transcript-active');
      transcriptToggle.setAttribute('title', i18nService.getString('showTranscript') || 'Show transcript');
      // If using aria-label instead of visible text
      if (transcriptToggle.hasAttribute('aria-label')) {
        transcriptToggle.setAttribute('aria-label', i18nService.getString('showTranscript') || 'Show transcript');
      }
    }
    
    // Always update the player toggle button
    const playerToggleBtn = document.getElementById('transcriptToggleBtn');
    if (playerToggleBtn) {
      playerToggleBtn.setAttribute('aria-expanded', 'false');
      playerToggleBtn.setAttribute('aria-pressed', 'false');
      playerToggleBtn.classList.remove('transcript-active');
      playerToggleBtn.setAttribute('title', i18nService.getString('showTranscript') || 'Show transcript');
      // If using aria-label instead of visible text
      if (playerToggleBtn.hasAttribute('aria-label')) {
        playerToggleBtn.setAttribute('aria-label', i18nService.getString('showTranscript') || 'Show transcript');
      }
    }
    
    // Return focus to appropriate toggle button
    if (focusTarget) {
      focusTarget.focus();
    } else if (playerToggleBtn) {
      // Prefer the player toggle button since it's now our primary toggle
      playerToggleBtn.focus();
    } else if (transcriptToggle && transcriptToggle.isConnected) {
      transcriptToggle.focus();
    }
    
    // Announce to screen readers - single announcement with distinctive message
    announceToScreenReader('Transcript hidden.');
  }
}

/**
 * Open transcript panel
 */
export function openTranscriptPanel(
  transcriptContainer: HTMLElement, 
  transcriptToggle: HTMLElement | null, 
  _heading: HTMLElement | null
): void {
  // Early return if already open
  if (!transcriptContainer.classList.contains('hidden')) return;
  
  // Update state using shared function - focus stays on toggle
  updateTranscriptPanelState(true, transcriptContainer, transcriptToggle, null);
  
  // No auto-focus on heading (removed as per requirements)
}

/**
 * Close transcript panel
 */
export function closeTranscriptPanel(
  transcriptContainer: HTMLElement, 
  transcriptToggle: HTMLElement | null, 
  focusTarget: HTMLElement | null
): void {
  // Early return if already closed
  if (transcriptContainer.classList.contains('hidden')) return;
  
  // Update state using shared function
  updateTranscriptPanelState(false, transcriptContainer, transcriptToggle, focusTarget);
}

/**
 * Toggle transcript panel (simplified to show/hide only)
 */
export function toggleTranscript(transcriptDiv: HTMLElement, transcriptToggle: HTMLElement): void {
  
  // Determine new state based on current visibility
  const newState = transcriptDiv.classList.contains('hidden');
  
  // Store scroll position before hiding
  if (!newState && appState.currentClip) {
    const transcriptPanel = transcriptDiv.querySelector<HTMLElement>('.transcript-panel');
    if (transcriptPanel && transcriptPanel.scrollTop > 0) {
      appState.transcriptScroll.set(appState.currentClip.id, transcriptPanel.scrollTop);
    }
  }
  
  // Update app state
  appState.transcriptVisible = newState;
  // Save preferences (will be called from main.js)
  const win = window as TranscriptWindow;
  if (win.saveTranscriptVisibility) {
    win.saveTranscriptVisibility(newState);
  }
  
  // Update UI state
  updateTranscriptPanelState(newState, transcriptDiv, transcriptToggle, transcriptToggle);
  
  // Log specific show/hide events
  if (newState) {
    logEvent(EVENT_TYPES['TRANSCRIPT_TOGGLE_SHOW']!, {
      clipId: appState.activeClipId
    });
    announceToScreenReader('Transcript shown.');
  } else {
    logEvent(EVENT_TYPES['TRANSCRIPT_TOGGLE_HIDE']!, {
      clipId: appState.activeClipId
    });
    announceToScreenReader('Transcript hidden.');
  }
  
  // Log general toggle event for compatibility
  logEvent(EVENT_TYPES['TRANSCRIPT_TOGGLE']!, {
    clipId: appState.activeClipId,
    visible: newState
  });
  
  // Restore scroll position if showing
  if (newState && appState.currentClip) {
    const transcriptPanel = transcriptDiv.querySelector<HTMLElement>('.transcript-panel');
    const savedScroll = appState.transcriptScroll.get(appState.currentClip.id);
    
    if (transcriptPanel && savedScroll && savedScroll > 0) {
      // Use double requestAnimationFrame to ensure DOM is fully updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (transcriptPanel) {
            transcriptPanel.scrollTop = savedScroll;
          }
        });
      });
    }
  }
}

/**
 * Helper to create a transcript toggle button for the player
 */
export function createTranscriptToggleButton(): HTMLButtonElement | null {
  // Only create if transcript is available for the current clip
  const win = window as TranscriptWindow;
  if (!win._transcriptElements) {
    logger.warn('Cannot create transcript toggle button: window._transcriptElements is not initialized');
    return null;
  }
  
  const { container } = win._transcriptElements;
  if (!container) {
    logger.warn('Cannot create transcript toggle button: container is not available in _transcriptElements');
    return null;
  }
  
  // Create a new button specifically for player controls
  const playerTranscriptBtn = document.createElement('button');
  playerTranscriptBtn.id = 'transcriptToggleBtn';
  playerTranscriptBtn.className = 'player-btn icon-only transcript-btn';
  
  // Use Heroicon chat bubble for transcript using helper function
  const icon = createSVGIcon('chat-bubble', 'player-icon');
  playerTranscriptBtn.appendChild(icon);
  
  // Set initial state based on appState
  const isOpen = !container.classList.contains('hidden');
  
  playerTranscriptBtn.setAttribute('aria-label', isOpen ? 'Hide transcript' : 'Show transcript');
  playerTranscriptBtn.setAttribute('title', isOpen ? 'Hide transcript' : 'Show transcript');
  playerTranscriptBtn.setAttribute('aria-pressed', isOpen ? 'true' : 'false');
  playerTranscriptBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  playerTranscriptBtn.setAttribute('aria-controls', 'transcriptDiv');
  
  // Update appState to match actual DOM state
  appState.transcriptPanelOpen = isOpen;
  
  // Sync with the original toggle button
  playerTranscriptBtn.addEventListener('click', () => {
    toggleTranscript(container, playerTranscriptBtn);
  });
  
  logger.debug('Transcript toggle button created successfully');
  return playerTranscriptBtn;
}

/**
 * Setup transcript (always accessible, starts closed)
 */
export function setupTranscriptGating(_audioEl: HTMLAudioElement, clip: Clip, audioSection: HTMLElement): void {
  // Don't proceed if there's no transcript
  if (!clip.transcript) return;
  
  // Render transcript but keep closed initially (will be toggled by user)
  if (!appState.transcriptInjected) {
    renderTranscript(clip, audioSection);
    appState.transcriptInjected = true;
    
    // Add transcript toggle button to player controls
    addTranscriptToggleToPlayer();
  }
}

/**
 * Function to add the transcript toggle button to player controls
 */
export function addTranscriptToggleToPlayer(): void {
  
  // Create transcript toggle button for the player controls
  const transcriptToggleBtn = createTranscriptToggleButton();
  
  // Find the right group in player controls
  const rightGroup = document.querySelector('.player-controls-right');
  
  if (rightGroup && transcriptToggleBtn) {
    // Ensure proper classes are set (icon-only style to match rewind/forward)
    transcriptToggleBtn.className = 'player-btn icon-only transcript-btn';
    rightGroup.appendChild(transcriptToggleBtn);
    logger.debug('Transcript toggle button added to player controls');
  } else {
    logger.warn('Could not add transcript button - rightGroup or button missing');
  }
}

/**
 * Function to render transcript with sentences if available
 */
export function renderTranscript(clip: Clip, _audioSection: HTMLElement): void {
  // Don't render anything if no transcript
  if (!clip.transcript) return;
  
  // Create hidden transcript toggle button (not added to DOM but used for state management)
  const transcriptToggle = document.createElement('button');
  transcriptToggle.id = 'transcriptToggle';
  transcriptToggle.setAttribute('aria-expanded', 'false');
  transcriptToggle.setAttribute('aria-controls', 'transcriptPanel');
  // This button is not visible but maintains state for backward compatibility
  
  // Create transcript container
  const transcriptContainer = document.createElement('div');
  transcriptContainer.id = 'transcriptContainer';
  transcriptContainer.className = 'transcript-container hidden';
  
  // Create transcript panel
  const transcriptPanel = document.createElement('div');
  transcriptPanel.id = 'transcriptPanel';
  transcriptPanel.className = 'transcript-panel';
  transcriptPanel.setAttribute('role', 'region');
  transcriptPanel.setAttribute('aria-label', 'Transcript');
  
  // Add panel heading
  const panelHeading = document.createElement('div');
  panelHeading.className = 'transcript-panel-header flex justify-between items-center mb-3';
  
  const heading = document.createElement('h2');
  heading.id = 'transcriptPanelHeading';
  heading.className = 'font-semibold text-lg';
  heading.textContent = i18nService.getString('transcript') || 'Transcript';
  heading.tabIndex = -1; // Make focusable for accessibility
  
  // Add close button
  const closeBtn = document.createElement('button');
  closeBtn.className = 'transcript-close-btn flex items-center justify-center p-1 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400';
  closeBtn.setAttribute('aria-label', 'Close transcript');
  closeBtn.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
    </svg>
  `;
  
  closeBtn.addEventListener('click', () => {
    const playerToggleBtn = document.getElementById('transcriptToggleBtn');
    closeTranscriptPanel(transcriptContainer, transcriptToggle, playerToggleBtn);
  });
  
  panelHeading.appendChild(heading);
  panelHeading.appendChild(closeBtn);
  
  // Add header to panel
  transcriptPanel.appendChild(panelHeading);
  
  // Create content container
  const transcriptContent = document.createElement('div');
  transcriptContent.className = 'transcript-content';
  
  // Auto-follow state machine for transcript scrolling (declare early for scope)
  let resumeFollowButton: HTMLButtonElement | null = null;
  
  // Create resume follow button (initially hidden)
  
  // R7: Enhanced manual scroll detection with pause/resume functionality
  let scrollDetectionTimeout: ReturnType<typeof setTimeout> | null = null;
  let lastScrollTop = 0;
  let programmaticScrollActive = false;
  let transcriptInitialized = false; // Flag to prevent false pause on initial scroll
  
  // Enhanced visual state tracking for lyrics-style highlighting (R6) - moved to outer scope
  let lastActiveIndex = -1;
  let autoFollowActive = true; // Track if auto-follow is active
  let centerZoneReached = false; // Track if we've reached the center zone
  let userScrollPaused = false; // Track if user scroll paused auto-follow
  
  // Forward declare the lyrics scroll function (defined later in the audio block)
  let performLyricsStyleScroll: ((activeSentence: HTMLElement, sentenceIndex: number) => void) | null = null;
  
  // R9: Reduced motion detection
  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  
  transcriptContent.addEventListener('scroll', () => {
    // Clear existing timeout
    if (scrollDetectionTimeout) {
      clearTimeout(scrollDetectionTimeout);
    }
    
    // Initialize scroll tracking on first scroll event
    if (!transcriptInitialized) {
      lastScrollTop = transcriptContent.scrollTop;
      transcriptInitialized = true;
      return; // Don't treat initial scroll as manual
    }
    
    // Debounce scroll detection
    scrollDetectionTimeout = setTimeout(() => {
      const currentScrollTop = transcriptContent.scrollTop;
      
      // Skip if this is a programmatic scroll from our auto-follow system
      if (programmaticScrollActive) {
        lastScrollTop = currentScrollTop;
        return;
      }
      
      // Detect manual user scroll
      const scrollDelta = Math.abs(currentScrollTop - lastScrollTop);
      const MANUAL_SCROLL_THRESHOLD = 15; // R7 threshold
      
      // Check if we're at the end of the transcript (prevent false pause)
      const isAtEnd = currentScrollTop >= (transcriptContent.scrollHeight - transcriptContent.clientHeight - 5);
      
      if (scrollDelta >= MANUAL_SCROLL_THRESHOLD && autoFollowActive && !userScrollPaused && !isAtEnd) {
        // User manually scrolled - pause auto-follow (R7)
        // Note: Don't pause if we're at the end of transcript
        userScrollPaused = true;
        autoFollowActive = false;
        
        // Create and show resume button
        const resumeBtn = createResumeFollowButton();
        if (resumeBtn) {
          resumeBtn.style.display = 'block';
        }
        
        // R8: Log pause event
        logEvent(EVENT_TYPES['TRANSCRIPT_FOLLOW_PAUSE_USER']!, {
          reason: 'manual_scroll',
          clipId: appState.activeClipId
        });
        
        // Announce to screen readers
        announceToScreenReader('Auto-follow paused. Resume?');
      }
      
      lastScrollTop = currentScrollTop;
    }, 100); // Shorter debounce for more responsive detection
  });
  
  // Add event listener for auto-follow reset (when seeking to beginning)
  transcriptContainer.addEventListener('resetAutoFollow', () => {
    // Reset all auto-follow state variables
    autoFollowActive = true;
    userScrollPaused = false;
    centerZoneReached = false;
    lastActiveIndex = -1;
    
    // Hide resume button if it exists
    if (resumeFollowButton) {
      resumeFollowButton.style.display = 'none';
    }
    
    // Reset scroll position to top
    transcriptContent.scrollTop = 0;
    lastScrollTop = 0;
    transcriptInitialized = false; // Allow re-initialization
  });
  
  // Enhanced resume follow button creation
  function createResumeFollowButton() {
    if (resumeFollowButton) return resumeFollowButton;
    
    resumeFollowButton = document.createElement('button');
    resumeFollowButton.className = 'resume-follow-btn';
    resumeFollowButton.textContent = 'Resume Follow';
    resumeFollowButton.setAttribute('aria-label', 'Resume auto-follow scrolling');
    resumeFollowButton.style.display = 'none';
    
    resumeFollowButton.addEventListener('click', () => {
      // R7: Resume auto-follow
      autoFollowActive = true;
      userScrollPaused = false;
      if (resumeFollowButton) {
        resumeFollowButton.style.display = 'none';
      }
      
      // Reset center zone to allow immediate scrolling
      centerZoneReached = true;
      
      // R8: Log resume event
      logEvent(EVENT_TYPES['TRANSCRIPT_FOLLOW_RESUME']!, {
        source: 'button',
        clipId: appState.activeClipId
      });
      
      // Force immediate re-center on current sentence
      const activeSentence = transcriptContent.querySelector<HTMLElement>('.t-line-active');
      if (activeSentence) {
        const sidxStr = activeSentence.dataset['sidx'];
        if (sidxStr) {
          const activeIndex = parseInt(sidxStr, 10);
          if (!isNaN(activeIndex)) {
            // Force immediate scroll without waiting for next sentence change
            setTimeout(() => {
              if (performLyricsStyleScroll) {
                performLyricsStyleScroll(activeSentence, activeIndex);
              }
            }, 100); // Small delay to ensure state is updated
          }
        }
      }
    });
    
    // Add to transcript panel header
    panelHeading.appendChild(resumeFollowButton);
    return resumeFollowButton;
  }
  
  // Check if we already have cached content for this clip in appState
  const cachedHtml = appState.transcriptCache.get(clip.id);
  if (cachedHtml) {
    // Use cached content
    transcriptContent.innerHTML = cachedHtml;
    
    // Reattach event listeners to sentences (word listeners are added via delegation later)
    const sentences = transcriptContent.querySelectorAll<HTMLElement>('.t-sentence');
    sentences.forEach(sentence => {
      const sidxStr = sentence.dataset['sidx'];
      const startStr = sentence.dataset['start'];
      const sidx = sidxStr ? parseInt(sidxStr, 10) : NaN;
      const start = startStr ? parseFloat(startStr) : NaN;
      
      // Make sure start time is set and visible for our click handler
      if (!isNaN(start)) {
        // Ensure the dataset attribute is visible for our click handler
        sentence.setAttribute('data-start', String(start));
        logger.debug('Set sentence with start time', { index: sidx, start });
        
        // Add replay button if not already present
        if (!sentence.querySelector('.t-replay-button')) {
          // Create replay button
          const replayButton = document.createElement('button');
          replayButton.className = 't-replay-button';
          replayButton.setAttribute('aria-label', `Replay sentence ${sidx + 1}`);
          replayButton.setAttribute('title', 'Replay this sentence');
          replayButton.setAttribute('type', 'button'); // Ensure it's a proper button
          replayButton.innerHTML = getReplayIconSVG();
          
          logger.debug('Created cached replay button', { sentenceIndex: sidx, timestamp: start });
          
          // Add click handler
          replayButton.addEventListener('click', function(e) {
            logger.debug('Cached replay button direct click handler (fallback)');
            
            e.preventDefault();
            e.stopPropagation();
            
            // Add visual feedback
            this.classList.add('clicked');
            setTimeout(() => {
              this.classList.remove('clicked');
              // Force hide the button if sentence is no longer active
              const currentSentenceEl = this.closest('.t-sentence');
              if (currentSentenceEl && !currentSentenceEl.matches(':hover') && 
                  !currentSentenceEl.classList.contains('current-sentence') && 
                  !currentSentenceEl.classList.contains('active')) {
                this.style.opacity = '0';
                this.style.backgroundColor = 'transparent';
              }
            }, 300);
            
            logger.debug('Cached fallback replay', { timestamp: start });
            
            // Log analytics event
            logEvent(EVENT_TYPES['TRANSCRIPT_SENTENCE_REPLAY_TRIGGER']!, {
              sentenceIndex: sidx,
              timestamp: start
            });
            
            // Try to play audio
            const audio = document.querySelector('audio');
            if (audio) {
              try {
                audio.currentTime = Math.max(0, Math.min(start, audio.duration || 9999));
                audio.play().catch(error => {
                  logger.error('Cached fallback play failed', error);
                  const playBtn = document.getElementById('playPauseBtn');
                  if (playBtn) playBtn.click();
                });
              } catch (error) {
                logger.error('Cached fallback audio control error', error);
              }
            }
            
            const win = window as TranscriptWindow;
            if (win.seekToTimestamp) {
              win.seekToTimestamp(start, { source: 'replay', sentenceIndex: sidx });
            }
          });
          
          // Wrap existing content in a text container if not already done
          if (!sentence.querySelector('.t-sentence-text')) {
            // Create text container
            const textContainer = document.createElement('span');
            textContainer.className = 't-sentence-text';
            
            // Move all existing content to text container except replay buttons
            while (sentence.firstChild) {
              const child = sentence.firstChild as HTMLElement;
              if (!child.classList || 
                  !child.classList.contains('t-replay-button')) {
                textContainer.appendChild(child);
              } else {
                sentence.removeChild(child);
              }
            }
            
            // Add text container to sentence first
            sentence.appendChild(textContainer);
          }
          
          // Add replay button after text container
          sentence.appendChild(replayButton);
        }
      }
      
      // Make sentence keyboard accessible
      sentence.setAttribute('tabindex', '0');
      sentence.setAttribute('role', 'button');
      sentence.setAttribute('aria-label', `Play from sentence ${sidx + 1}`);
      
      // Add enhanced keyboard event listeners for accessibility
      sentence.addEventListener('keydown', function(e: KeyboardEvent) {
        // On Enter or Space, focus the replay button instead of playing directly
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          
          // Find and focus the replay button if it exists
          const replayButton = sentence.querySelector<HTMLButtonElement>('.t-replay-button');
          if (replayButton) {
            // Log keyboard navigation event
            logEvent(EVENT_TYPES['TRANSCRIPT_SENTENCE_KEYBOARD_FOCUS']!, {
              sentenceIndex: sidx,
              key: e.key
            });
            
            replayButton.focus();
          }
        }
        
        // Focus replay button on Tab + R (alternative keyboard shortcut)
        else if (e.key === 'r' && !e.ctrlKey && !e.altKey && !e.metaKey) {
          e.preventDefault();
          
          // Find and focus the replay button if it exists
          const replayButton = sentence.querySelector<HTMLButtonElement>('.t-replay-button');
          if (replayButton) {
            replayButton.focus();
          }
        }
      });
    });
  } else if (clip.sentences && Array.isArray(clip.sentences) && clip.sentences.length > 0) {
    // Create temporary container to build markup
    const tempContainer = document.createElement('div');
    
    clip.sentences.forEach((sentence, idx) => {
      // Use our consistent sentence element creator for all sentences
      const sentenceEl = createConsistentSentenceElement(sentence, idx);
      sentenceEl.className = 't-sentence sentence'; // Add the extra 'sentence' class
      
      // Add to the temporary container
      tempContainer.appendChild(sentenceEl);
    });
    
    // Cache the markup for future use
    const renderedHtml = tempContainer.innerHTML;
    appState.transcriptCache.set(clip.id, renderedHtml);
    
    // Manage cache size to prevent memory bloat
    manageTranscriptCacheSize();
    
    // Set the content
    transcriptContent.innerHTML = renderedHtml;
    
    // Reset any highlighting on all sentences as a precaution
    transcriptContent.querySelectorAll('.t-sentence').forEach(s => {
      s.classList.remove('current-sentence', 'active', 'clicked', 'focused');
    });
    
    // Add timeupdate listener to track current sentence
    const audio = document.querySelector<HTMLAudioElement>('audio');
    if (audio) {
      let currentSentenceIndex = -1;
      
      // Clean up any existing listeners first
      const audioWithTracker1 = audio as HTMLAudioElement & { _sentenceTracker?: () => void };
      if (audioWithTracker1._sentenceTracker) {
        audio.removeEventListener('timeupdate', audioWithTracker1._sentenceTracker);
      }
      
      // Variables for throttling highlight updates
      let lastHighlightTime = 0;
      // Only need a scheduled flag for throttling, pendingHighlightIndex was unused
      let highlightUpdateScheduled = false;
      
      // R6: Enhanced visual state update function with lyrics-style highlighting
      function updateTranscriptVisualState(activeIndex: number): void {
        // Early return if same index (R6 requirement)
        if (activeIndex === lastActiveIndex) {
          return;
        }
        
        const allSentences = transcriptContent.querySelectorAll<HTMLElement>('.t-sentence');
        if (!allSentences.length) return;
        
        // Apply classes to ALL sentences for proper opacity gradient
        allSentences.forEach((line, index) => {
          // Clear existing classes
          line.classList.remove('t-line-active', 't-line-prev-1', 't-line-prev-2', 
                                't-line-prev-far', 't-line-next-1', 't-line-next-2', 
                                't-line-next-far', 'current-sentence', 'active');
          
          if (activeIndex >= 0) {
            const distance = Math.abs(index - activeIndex);
            
            if (index === activeIndex) {
              // Active line (R4: opacity 1.0)
              line.classList.add('t-line-active', 'current-sentence', 'active');
            } else if (index < activeIndex) {
              // Previous lines
              if (distance === 1) {
                line.classList.add('t-line-prev-1'); // R4: opacity 0.75
              } else if (distance === 2) {
                line.classList.add('t-line-prev-2'); // R4: opacity 0.55
              } else if (distance > 2) {
                line.classList.add('t-line-prev-far'); // R4: opacity 0.35
              }
            } else {
              // Next lines  
              if (distance === 1) {
                line.classList.add('t-line-next-1'); // R4: opacity 0.85
              } else if (distance === 2) {
                line.classList.add('t-line-next-2'); // R4: opacity 0.70
              } else if (distance > 2) {
                line.classList.add('t-line-next-far'); // R4: opacity 0.55
              }
            }
          }
        });
        
        // Update tracking variables
        lastActiveIndex = activeIndex;
        
        // R2: Center-zone detection and auto-scroll logic
        if (activeIndex >= 0 && activeIndex < allSentences.length && autoFollowActive && !userScrollPaused) {
          const activeSentence = allSentences[activeIndex];
          if (!activeSentence) return;
          const containerRect = transcriptContent.getBoundingClientRect();
          const sentenceRect = activeSentence.getBoundingClientRect();
          
          const containerCenter = containerRect.top + (containerRect.height / 2);
          const sentenceCenter = sentenceRect.top + (sentenceRect.height / 2);
          
          // Check if we've reached the center zone
          if (!centerZoneReached) {
            if (sentenceCenter >= containerCenter) {
              centerZoneReached = true; // Now start auto-scrolling
            }
          }
          
          // R2: Only scroll once center zone is reached
          if (centerZoneReached && performLyricsStyleScroll) {
            performLyricsStyleScroll(activeSentence, activeIndex);
          }
        }
      }
      
      // R2: Lyrics-style scroll function with center positioning
      performLyricsStyleScroll = function(activeSentence: HTMLElement, sentenceIndex: number): void {
        const containerRect = transcriptContent.getBoundingClientRect();
        const sentenceRect = activeSentence.getBoundingClientRect();
        
        const containerCenter = containerRect.top + (containerRect.height / 2) + TRANSCRIPT_CENTER_OFFSET_PX;
        const sentenceCenter = sentenceRect.top + (sentenceRect.height / 2);
        
        // R10: Skip scroll if already within ±12px of ideal center
        const centerDiff = Math.abs(sentenceCenter - containerCenter);
        if (centerDiff <= 12) {
          return; // No jitter - already centered
        }
        
        // Calculate scroll adjustment to center the active sentence
        const scrollAdjustment = sentenceCenter - containerCenter;
        const newScrollTop = Math.max(0, transcriptContent.scrollTop + scrollAdjustment);
        
        // R9: Respect reduced motion preference
        const smooth = !prefersReducedMotion();
        
        try {
          // Set programmatic scroll flag to prevent false manual scroll detection
          programmaticScrollActive = true;
          
          if (smooth) {
            transcriptContent.scrollTo({
              top: newScrollTop,
              behavior: 'smooth'
            });
            
            // For smooth scrolling, clear flag after animation completes
            setTimeout(() => {
              programmaticScrollActive = false;
            }, TRANSCRIPT_SCROLL_DURATION_MS + 100);
          } else {
            transcriptContent.scrollTop = newScrollTop;
            
            // For instant scrolling, clear flag immediately
            setTimeout(() => {
              programmaticScrollActive = false;
            }, 50);
          }
          
          // Update lastScrollTop to prevent false detection
          setTimeout(() => {
            lastScrollTop = newScrollTop;
          }, smooth ? TRANSCRIPT_SCROLL_DURATION_MS + 120 : 70);
          
          // R8: Log scroll event
          logEvent(EVENT_TYPES['TRANSCRIPT_FOLLOW_SCROLL']!, {
            clipId: appState.activeClipId,
            fromIndex: lastActiveIndex,
            toIndex: sentenceIndex,
            smooth: smooth
          });
        } catch (error) {
          // R14: Fallback on error
          logger.warn('transcript: Scroll error, falling back to legacy highlight', error);
          activeSentence.classList.add('current-sentence', 'active');
          programmaticScrollActive = false;
        }
      }
      
      // Create new tracker function
      const audioWithTracker2 = audio as HTMLAudioElement & { _sentenceTracker?: () => void };
      audioWithTracker2._sentenceTracker = function() {
        // Don't proceed if no transcript
        if (!appState.transcriptInjected) {
          return;
        }
        
        const currentTime = audio.currentTime;
        const sentences = clip.sentences;
        if (!sentences) return;
        let newIndex = -1;
        
        // Find the current sentence based on timestamps
        for (let i = 0; i < sentences.length; i++) {
          const sentence = sentences[i];
          if (!sentence) continue;
          const startTime = sentence.start;
          // Use end timestamp if available, otherwise fall back to next sentence start time
          const nextSentence = sentences[i + 1];
          const endTime = sentence.end !== undefined ? 
                          sentence.end : 
                          (nextSentence ? nextSentence.start : Infinity);
          
          if (currentTime >= startTime && currentTime < endTime) {
            newIndex = i;
            break;
          }
        }
        
        // If we're past all timestamps but still playing, highlight last sentence
        const lastSentence = sentences[sentences.length - 1];
        if (newIndex === -1 && lastSentence && currentTime > lastSentence.start) {
          newIndex = sentences.length - 1;
        }
        
        // Debug the current time and matched sentence index
        logger.debug('Audio time and matched sentence', { 
          currentTime: currentTime.toFixed(2), 
          matchedSentence: newIndex 
        });
        
        // Always update the tracking index and use enhanced visual state system
        currentSentenceIndex = newIndex;
        
        // Calculate throttle timing
        const now = Date.now();
        const timeSinceLastUpdate = now - lastHighlightTime;
        const throttleInterval = 1000 / SENTENCE_HIGHLIGHT_FPS;
        
        // Throttle DOM updates to specified FPS using enhanced visual state system
        if (!highlightUpdateScheduled && timeSinceLastUpdate >= throttleInterval) {
          // If enough time has passed, update immediately with enhanced system
          updateTranscriptVisualState(currentSentenceIndex);
          lastHighlightTime = now;
          highlightUpdateScheduled = false;
        } else if (!highlightUpdateScheduled) {
          // Schedule an update for later using enhanced system
          highlightUpdateScheduled = true;
          setTimeout(() => {
            updateTranscriptVisualState(currentSentenceIndex);
            lastHighlightTime = Date.now();
            highlightUpdateScheduled = false;
          }, throttleInterval - timeSinceLastUpdate);
        }
      };
      
      // Add the listener
      const audioWithTracker3 = audio as HTMLAudioElement & { _sentenceTracker?: () => void };
      if (audioWithTracker3._sentenceTracker) {
        audio.addEventListener('timeupdate', audioWithTracker3._sentenceTracker);
      }
    }
  } else if (clip.transcript && !clip.sentences) {
    // Use transcript array directly (deprecated field, fallback)
    const sentences = clip.transcript;
    
    if (sentences && sentences.length > 0) {
      sentences.forEach((sentence: ParsedSentence | TranscriptSentence, idx: number) => {
        const sentenceEl = document.createElement('div'); // Changed from span to div for block display
        sentenceEl.className = 't-sentence sentence';
        sentenceEl.dataset['sidx'] = String(idx);
        
        // Make sentence keyboard accessible
        sentenceEl.setAttribute('tabindex', '0');
        sentenceEl.setAttribute('role', 'button');
        sentenceEl.setAttribute('aria-label', `Play from sentence ${idx + 1}`);
        
        // Add timestamp data (using approximation based on index if actual timestamp not available)
        const transcriptSentence = sentence as TranscriptSentence;
        if (transcriptSentence.start !== undefined) {
          sentenceEl.dataset['start'] = String(transcriptSentence.start);
        }
        
        // Remove automatic click behavior - now only replay button triggers audio
        if (transcriptSentence.start !== undefined) {
          sentenceEl.addEventListener('click', function(this: HTMLDivElement, e: MouseEvent) {
            // No longer seeking on sentence click
            // Log analytics event for monitoring this interaction change
            if (e.target === this) {
              logger.debug('Sentence clicked - no action taken');
              logEvent(EVENT_TYPES['TRANSCRIPT_SENTENCE_TEXT_CLICKED']!, {
                sentenceIndex: idx,
                timestamp: new Date().toISOString()
              });
            }
          });
          
          // Update keyboard event listeners to focus replay button instead of playing
          sentenceEl.addEventListener('keydown', function(e: KeyboardEvent) {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              
              // Find and focus the replay button if it exists
              const replayButton = sentenceEl.querySelector<HTMLButtonElement>('.t-replay-button');
              if (replayButton) {
                replayButton.focus();
              }
            }
          });
        }
        
        // Split text into words for word-level markup
        const words = sentence.text.split(/\s+/);
        words.forEach((word: string, widx: number) => {
          // Create word span
          const wordEl = document.createElement('span');
          wordEl.className = 't-word';
          wordEl.dataset['widx'] = String(widx);
          wordEl.dataset['word'] = word.replace(/[^\w'-]/g, ''); // Store clean word without punctuation
          wordEl.textContent = word + (widx < words.length - 1 ? ' ' : '');
          
          // No direct event listeners - using delegation via panel
          
          // Add to sentence
          sentenceEl.appendChild(wordEl);
        });
        
        transcriptContent.appendChild(sentenceEl);
      });
    }
  }
  
  // Add the transcript content to panel
  transcriptPanel.appendChild(transcriptContent);
  
  // Add the transcript panel to the container
  transcriptContainer.appendChild(transcriptPanel);
  
  // Add toggle behavior
  transcriptToggle.addEventListener('click', () => {
    toggleTranscript(transcriptContainer, transcriptToggle);
  });
  
  // Add escape key handler
  if (handleEscapeKey) {
    document.addEventListener('keydown', handleEscapeKey);
  }
  
  // Add a click handler to the transcript panel if not already bound
  const panelWithHandler = transcriptPanel as HTMLDivElement & { _hasClickHandler?: boolean };
  if (!panelWithHandler._hasClickHandler) {
    // Store mousedown position locally in the panel for selection vs click detection
    // This avoids using global variables and is more maintainable
    let mouseDownPosition: { x: number; y: number; time: number } | null = null;
    
    transcriptPanel.addEventListener('mousedown', function(e: MouseEvent) {
      // Store the initial position to compare on click
      mouseDownPosition = { x: e.clientX, y: e.clientY, time: Date.now() };
    });
    
    transcriptPanel.onclick = function(e: MouseEvent) {
      // Check if there's an active text selection
      const sel = window.getSelection();
      const textSelected = sel ? sel.toString().length > 0 : false;
      
      // Check for significant mouse movement (more than 6px in any direction)
      let significantMovement = false;
      if (mouseDownPosition) {
        const moveX = Math.abs(e.clientX - mouseDownPosition.x);
        const moveY = Math.abs(e.clientY - mouseDownPosition.y);
        significantMovement = (moveX > 6 || moveY > 6);
        
        // Reset for next interaction
        mouseDownPosition = null;
      }
      
      // If text was selected or there was significant movement, don't treat as a click
      if (textSelected || significantMovement) {
        return;
      }
      
      // Handle direct word clicks
      const target = e.target as HTMLElement;
      const wordEl = target.closest<HTMLElement>('.t-word');
      if (wordEl) {
        // Only stop propagation if this is a genuine click (not text selection)
        e.stopPropagation(); // Prevent triggering the sentence click
        const sentenceEl = wordEl.closest<HTMLElement>('.t-sentence');
        if (!sentenceEl) return;
        const sidxStr = sentenceEl.dataset['sidx'];
        const widxStr = wordEl.dataset['widx'];
        const word = wordEl.dataset['word'];
        if (!sidxStr || !widxStr || !word) return;
        const sidx = parseInt(sidxStr, 10);
        const widx = parseInt(widxStr, 10);
        
        // Log word interaction
        logger.info('word stub', { word, widx, sidx });
        
        // Call the word interaction handler
        onWordInteract({ 
          word: word, 
          wordIndex: widx,
          sentenceIndex: sidx
        });
        return; // Don't process as sentence click
      }
      
      // Check if clicked on a replay button or any element inside it (like SVG path) - skip further processing
      const replayButtonTarget = e.target as HTMLElement;
      const replayButton = replayButtonTarget.closest<HTMLButtonElement>('.t-replay-button');
      if (replayButton) {
        logger.debug('Transcript panel: Detected click on replay button, handling directly', { element: replayButton });
        
        // Get sentence data from the button's parent sentence
        const sentenceEl = replayButton.closest<HTMLElement>('.t-sentence');
        if (sentenceEl) {
          const sidxStr = sentenceEl.dataset['sidx'];
          const startStr = sentenceEl.dataset['start'];
          if (!sidxStr || !startStr) return;
          const sentenceIndex = parseInt(sidxStr, 10);
          const timestamp = parseFloat(startStr);
          
          logger.debug('Direct replay', { sentenceIndex, timestamp });
          
          // Add visual feedback
          replayButton.classList.add('clicked');
          setTimeout(() => {
            replayButton.classList.remove('clicked');
            // Force hide the button if sentence is no longer active
            const currentSentenceEl = replayButton.closest('.t-sentence');
            if (currentSentenceEl && !currentSentenceEl.matches(':hover') && 
                !currentSentenceEl.classList.contains('current-sentence') && 
                !currentSentenceEl.classList.contains('active')) {
              replayButton.style.opacity = '0';
              replayButton.style.backgroundColor = 'transparent';
            }
          }, 300);
          
          // Log analytics event
          logEvent(EVENT_TYPES['TRANSCRIPT_SENTENCE_REPLAY_TRIGGER']!, {
            sentenceIndex: sentenceIndex,
            timestamp: timestamp
          });
          
          // Get audio element and play directly
          const audio = document.querySelector('audio');
          logger.debug('Direct replay - Audio element found', { found: !!audio });
          
          if (audio) {
            logger.debug('Direct replay - Audio state', {
              src: audio.src,
              currentTime: audio.currentTime,
              duration: audio.duration,
              paused: audio.paused,
              readyState: audio.readyState
            });
            
            try {
              // Set time and try to play
              const safeTime = Math.max(0, Math.min(timestamp, audio.duration || 9999));
              logger.debug('Direct replay - Setting currentTime', { safeTime });
              audio.currentTime = safeTime;
              
              logger.debug('Direct replay - Attempting to play audio');
              const playPromise = audio.play();
              
              if (playPromise !== undefined) {
                playPromise.then(() => {
                  logger.debug('Direct replay - Audio play succeeded');
                }).catch(error => {
                  logger.error('Direct replay - Audio play promise failed', error);
                  // Try play button as fallback
                  const playBtn = document.getElementById('playPauseBtn');
                  logger.debug('Direct replay - Trying play button fallback', { found: !!playBtn });
                  if (playBtn) {
                    logger.debug('Direct replay - Clicking play button');
                    playBtn.click();
                  }
                });
              } else {
                logger.debug('Direct replay - Play method returned undefined');
              }
            } catch (error) {
              logger.error('Direct replay - Error in audio control', error);
              const playBtn = document.getElementById('playPauseBtn');
              if (playBtn) {
                logger.debug('Direct replay - Exception fallback - clicking play button');
                playBtn.click();
              }
            }
          } else {
            logger.error('Direct replay - No audio element found');
          }
          
          // Also call seekToTimestamp for consistency
          logger.debug('Direct replay - Calling seekToTimestamp');
          const win = window as TranscriptWindow;
          if (win.seekToTimestamp) {
            win.seekToTimestamp(timestamp, { source: 'replay', sentenceIndex: sentenceIndex });
          }
        }
        
        return; // Don't process further
      }
      
      // Handle sentence clicks - but don't trigger audio (now only replay button does that)
      const sentenceTarget = e.target as HTMLElement;
      const sentenceEl = sentenceTarget.closest<HTMLElement>('.t-sentence');
      const sel2 = window.getSelection();
      const hasSelection = sel2 ? sel2.toString().length > 0 : false;
      
      if (sentenceEl && !hasSelection && !significantMovement) {
        // Check if we clicked on the text container (or any element that's not a button)
        const isClickOnText = sentenceTarget.closest('.t-sentence-text') || 
                             sentenceTarget === sentenceEl;
                             
        if (!isClickOnText) {
          return; // Only process clicks on text, not on buttons
        }
        
        logger.debug('Sentence element clicked - no audio triggered');
        
        // Track sentence text click in analytics with new event name
        const sidxStr = sentenceEl.dataset['sidx'];
        if (sidxStr) {
          logEvent(EVENT_TYPES['TRANSCRIPT_SENTENCE_TEXT_CLICKED']!, {
            sentenceIndex: parseInt(sidxStr, 10),
            timestamp: new Date().toISOString()
          });
        }
        
        // No longer playing audio on sentence click
        // This functionality is now handled exclusively by the replay button
      }
    };
    
    // Mark as bound to prevent duplicate binding
    panelWithHandler._hasClickHandler = true;
  }

  // Store reference for other components to access
  const win = window as TranscriptWindow;
  win._transcriptElements = {
    toggle: transcriptToggle,
    container: transcriptContainer
  };
  
  // Always append transcript container to body (overlay mode for all breakpoints)
  transcriptContainer.classList.remove('in-column');
  transcriptContainer.classList.add('fixed-overlay');
  document.body.appendChild(transcriptContainer);
  
  // Note: The toggle button is not appended here anymore
  // It will be added to the player controls in createPlayerControlsRow
}

// Export all functions as a module interface
export default {
  getReplayIconSVG,
  manageTranscriptCacheSize,
  parseTranscriptIntoSentences,
  backfillSentenceEndTimestamps,
  onWordInteract,
  announceToScreenReader,
  createConsistentSentenceElement,
  removeTranscriptEventListeners,
  createEscapeKeyHandler,
  updateTranscriptPanelState,
  openTranscriptPanel,
  closeTranscriptPanel,
  toggleTranscript,
  createTranscriptToggleButton,
  setupTranscriptGating,
  addTranscriptToggleToPlayer,
  renderTranscript
};
